/**
 * dialogueScript.ts -- All game dialogue copy as typed constants (Phase 44).
 *
 * RULE: No dialogue strings belong in game logic files (GameCanvas.tsx, character.ts).
 * Import constants from here; call queueLine() with them at trigger points.
 */

/** SCRIPT-01: Intro sequence -- 5 lines queued on first game start */
export const INTRO_LINES: readonly string[] = [
  "oh. a visitor.",
  "you found the secret layer.",
  "let's see how long you last.",
  "*(beat)*",
  "type the words. they're from your own portfolio.",
];

/** SCRIPT-02: Player-hit reaction pool (random selection) */
export const PLAYER_HIT_POOL: readonly string[] = [
  "oof.",
  "that's gonna hurt.",
  "you okay?",
  "stay focused.",
  "they got you.",
];

/**
 * SCRIPT-02: Tier transition lines.
 * Typed as Record<2|3|4, string> so tier 5 has NO entry at compile time (SCRIPT-03).
 * Caller must guard with `if (newTier in TIER_LINES)` before accessing.
 */
export const TIER_LINES: Readonly<Record<2 | 3 | 4, string>> = {
  2: "picking up speed.",
  3: "it's getting intense.",
  4: "...hold on.",
};

/** SCRIPT-04: Exit dialogue -- queued when player chooses [exit] */
export const EXIT_LINES: readonly string[] = [
  "alright. that's enough.",
  "come back sometime.",
];

/** SCRIPT-04: Death dialogue pool -- one random line queued when lives reach 0 */
export const DEATH_POOL: readonly string[] = [
  "happens to everyone.",
  "welp.",
  "that's rough.",
  "oh no.",
  "ouch. that's a wipe.",
  "rip.",
];

/** SCRIPT-07: Pause quips — fire when player presses ESC to pause */
export const PAUSE_QUIPS: readonly string[] = [
  "taking a breather?",
  "they'll be waiting when you get back.",
  "pausing won't make them disappear.",
  "rest up. they're patient.",
  "i'll wait.",
];

/** SCRIPT-05: Long survival -- fires once at wave 10+ */
export const LONG_SURVIVAL_LINE = "...you're still here." as const;

/** SCRIPT-06: Idle quips — cursor hasn't moved, no enemies nearby */
export const IDLE_QUIPS: readonly string[] = [
  "you just gonna stand there?",
  "i can wait all day.",
  "hello??",
  "...you alive?",
  "not moving helps nobody.",
];

/** SCRIPT-06: Idle quips — cursor hasn't moved, enemies ARE nearby */
export const IDLE_DANGER_QUIPS: readonly string[] = [
  "they're RIGHT THERE.",
  "you see them coming, right?",
  "now would be a good time.",
  "hello? words incoming.",
  "MOVE.",
];

/** SCRIPT-06: Movement quips — moving but not typing */
export const MOVEMENT_QUIPS: readonly string[] = [
  "stop running and FIGHT.",
  "you can't dodge forever.",
  "they're not going to type themselves.",
  "the words follow your cursor, you know.",
  "running is a strategy. a bad one.",
];

/** SCRIPT-06: Near-miss quips — enemy just barely missed the cursor */
export const NEAR_MISS_QUIPS: readonly string[] = [
  "that was INCHES away.",
  "okay that one nearly got you.",
  "TYPE IT.",
  "woah.",
  "eyes on the words.",
];

/** SCRIPT-06: Tier-change quips */
export const TIER_CHANGE_QUIPS: Readonly<Record<2 | 3 | 4 | 5, string[]>> = {
  2: [
    "they're moving faster now.",
    "you cleared enough to speed this up.",
    "wave two energy.",
  ],
  3: [
    "okay i'm actually watching now.",
    "tier three. things get ugly from here.",
    "you've survived longer than expected.",
  ],
  4: [
    "this is where most people stop.",
    "i didn't think you'd last this long.",
    "tier four. good luck.",
  ],
  5: [
    "maximum. that's it. that's all i've got.",
    "i honestly didn't think you'd get here.",
    "tier five. it's chaos now.",
  ],
};

/** SCRIPT-06: Wave-clear quips — new wave begins */
export const WAVE_CLEAR_QUIPS: readonly string[] = [
  "wave cleared. more incoming.",
  "you actually did it.",
  "one wave down.",
  "next wave. don't get comfortable.",
  "still alive. good.",
];

/** SCRIPT-06: General ambient quips — fire during normal play */
export const GENERAL_QUIPS: readonly string[] = [
  "these are YOUR words, by the way.",
  "i'm watching.",
  "you built the thing you're defending against.",
  "a portfolio that fights back.",
  "interesting way to review your own work.",
  "not bad. keep typing.",
];

/**
 * Returns a contextual word-cleared quip that sometimes references the typed word.
 * ~40% chance to include the word, otherwise uses a generic reaction.
 */
export function getWordClearedQuip(word: string): string {
  // Portfolio-specific word reactions
  const SPECIFIC: Readonly<Record<string, string>> = {
    canvas:     "canvas — you wrote a game in canvas. that's funny.",
    react:      "react. this whole thing is built in react.",
    javascript: "javascript. interesting choice.",
    portfolio:  "typing your own portfolio at me.",
    game:       "it IS a game. that's the bit.",
    design:     "design? look around. i made this.",
    project:    "your own projects, coming back for you.",
    skill:      "skill? we'll see.",
    developer:  "developer. prove it.",
    engineer:   "engineer. same thing.",
    website:    "the website is attacking you. classic.",
    code:       "code that attacks the coder.",
    type:       "you're literally typing the word 'type'.",
    word:       "the word 'word.' recursive.",
  };

  const lower = word.toLowerCase();
  if (SPECIFIC[lower]) return SPECIFIC[lower];

  // 40% chance to use word in a template
  if (Math.random() < 0.4) {
    const templates = [
      `${word} — gone.`,
      `${word}. done.`,
      `you know ${word}?`,
      `${word} cleared.`,
    ];
    return templates[Math.floor(Math.random() * templates.length)];
  }

  // Generic fallback
  const generic = [
    "nice.",
    "keep going.",
    "clean.",
    "that one's gone.",
    "good.",
  ];
  return generic[Math.floor(Math.random() * generic.length)];
}

/**
 * SCRIPT-08: Safe zone activation quips — one per use, index 0-2.
 * Index matches safeZoneUses value BEFORE incrementing (use 1 = index 0).
 * Locked in 48-CONTEXT.md per user spec.
 */
export const SAFE_ZONE_QUIPS: readonly string[] = [
  "fine. i got you.",
  "again??",
  "last time. for real.",
];

/**
 * SCRIPT-08: Floating taunt quip — fires on 4th safe zone entry attempt,
 * when dialogue box is fully gone (safeZoneUses === 3).
 * Rendered in a dedicated floating box by safeZone.ts (not the normal dialogue queue).
 */
export const SAFE_ZONE_TAUNT = "hah. thought i couldn't talk anymore huh." as const;

/** SCRIPT — Phase 49: Discovery hint when player first hides cursor in a text selection zone */
export const SELECTION_DISCOVERY_HINT = "clever. text selections are walls here." as const;
